<template>
    <div
        v-if="!activeOrganization?.is_legacy_billing && getSubscriptionPlan !== 'Enterprise' && profileUnlocksAllocated > 0"
        class="space-y-4 py-2 text-white "
    >
        <Card
            v-if="props.sidebarOpen"
            class="mx-3 rounded-lg border border-cinder-700 bg-cinder-900 shadow-sm animate-in slide-in-from-left fade-in duration-300 flex items-center justify-start overflow-hidden"
        >
            <CardContent class="w-full space-y-4 p-3">
                <div
                    class="space-y-3"
                >
                    <div class="flex items-center justify-between">
                        <span class="text-xs uppercase tracking-wide text-zinc-400">
                            Profile Unlocks
                        </span>
                        <span class="text-xs font-medium text-zinc-500">
                            {{ $numeral(profileUnlocksUsed).format('0,0') }} /
                            {{ $numeral(profileUnlocksAllocated).format('0,0') }}
                        </span>
                    </div>
                    <ProgressBar
                        :model-value="(profileUnlocksUsed / profileUnlocksAllocated) * 100"
                        class="h-1.5 bg-zinc-700"
                    />
                </div>

                <div
                    v-if="reportGenerationAllocated > 0 && hasReports"
                    class="space-y-3"
                >
                    <div class="flex items-center justify-between">
                        <span class="text-xs uppercase tracking-wide text-zinc-400">
                            Reports
                        </span>
                        <span class="text-xs font-medium text-zinc-500">
                            {{ $numeral(reportGenerationUsed).format('0,0') }} /
                            {{ $numeral(reportGenerationAllocated).format('0,0') }}
                        </span>
                    </div>
                    <ProgressBar
                        :model-value="(reportGenerationUsed / reportGenerationAllocated) * 100"
                        class="h-1.5 bg-zinc-700"
                    />
                </div>

                <p v-if="hasAnyQuotaAllocated" class="mt-2 text-xs text-white/90">
                    Monthly usage resets in {{ daysRemaining }} days
                </p>

                <button
                    class="w-full mx-auto rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                    @click="upgradePlan"
                >
                    Upgrade Now <span class="ml-1">🚀</span>
                </button>
            </CardContent>
        </Card>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import Card from '@/components/ui/card/Card.vue'
import CardContent from '@/components/ui/card/CardContent.vue'
import { Progress as ProgressBar } from '@/components/ui/progress'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import _ from 'lodash'
import { trackEvent, AnalyticalEvents } from '@/utils/analytics'

const router = useRouter()
const store = useStore()
const activeOrganizationId = computed(() => store.getters.selfActiveOrganizationId)
const activeOrganization = computed(() => store.getters.selfActiveOrganization)

const profileUnlocksUsed = computed(() => store.getters.quotaUsed({ quotaType : 'profileUnlocks'}))
const profileUnlocksAllocated = computed(() => store.getters.quotaAllocated({ quotaType : 'profileUnlocks'}))

const reportGenerationUsed = computed(() => store.getters.quotaUsed({ quotaType : 'reportGeneration'}))
const reportGenerationAllocated = computed(() => store.getters.quotaAllocated({ quotaType : 'reportGeneration'}))

const resetDate = computed(() => store.getters.quotaResetDate)
const subscriptionTier = computed(() => store.getters.selfSubscriptionTier)
const hasReports = computed(() => store.getters.checkFeatureEnabled('module.reports'))
const hasAnyQuotaAllocated = computed(() => {
    return profileUnlocksAllocated.value > 0 || (reportGenerationAllocated.value > 0 && hasReports.value)
})

const props = defineProps({
    sidebarOpen: Boolean,
})

function upgradePlan() {
    // Trigger upgrade CTA click event
    trackEvent(AnalyticalEvents.SalesUpgradeCtaClicked, {
        from_tier: subscriptionTier.value,
    })

    router.push({
        name: 'organization-subscriptions',
        params: { organization_id: activeOrganizationId.value },
    })
}

const getSubscriptionPlan = computed(() => {
    if (activeOrganization.value?.is_legacy_billing) return 'Legacy Plan'

    if (!subscriptionTier.value) return 'Free'

    return store.getters.getFromDictionary(`products.${subscriptionTier.value}.label`) || 'Free'
})

const daysRemaining = computed(() => {
    const now = new Date()
    const endDate = new Date(resetDate.value)
    const diffTime = Math.max(0, endDate - now)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
})
</script>
